import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import * as z from "zod";
import { useEffect } from "react";
import { useEmploymentStatusTypes } from "@src/api/useEmploymentStatusTypes";
import Container from "@src/components/shared/Container";
import TitleAndDesc from "@src/components/shared/TitleAndDesc";
import { Button } from "@src/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@src/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@src/components/ui/radio-group";
import { Skeleton } from "@src/components/ui/skeleton";
import useZustandStore from "@src/helpers/zustand";
import { cn } from "@src/lib/utils";
import { useKYCAddUserLog, LoggerTypes } from "@src/api/useKYCAddUserLog";

const FormSchema = z.object({
  employment: z.string().refine((v) => v),
});

function Employment() {
  const addUserLogMutation = useKYCAddUserLog();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: zData, setData } = useZustandStore((state) => state);
  const employments = useEmploymentStatusTypes();

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      employment: zData.employment.type,
    },
  });

  useEffect(() => {
    addUserLogMutation.mutate({
      eventName: LoggerTypes.Employment,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSubmit(data: z.infer<typeof FormSchema>) {
    setData({
      ...zData,
      employment: {
        ...zData.employment,
        type: data.employment,
      },
    });

    if (data.employment === "Employed") {
      navigate(`/${i18n.language}/employment/details`);
    } else if (data.employment === "Self-employed") {
      navigate(`/${i18n.language}/employment/self`);
    } else if (data.employment === "BusinessOwner") {
      navigate(`/${i18n.language}/employment/business`);
    } else {
      navigate(`/${i18n.language}/source-of-income`);
    }
  }

  return (
    <Container className="max-md:min-h-[90vh] max-md:bg-white">
      <Form {...form}>
        <div className="py-4">
          <TitleAndDesc
            desc={t("employment.desc")}
            title={t("employment.title")}
          />
        </div>
        <form
          className="flex flex-col gap-3 max-md:mb-40"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <FormField
            control={form.control}
            name="employment"
            render={({ field }) => {
              return (
                <FormItem>
                  <RadioGroup
                    className="rounded-lg border border-solid border-gray-200 bg-white gap-0"
                    onValueChange={(e) => {
                      field.onChange(e);
                      setData({
                        ...zData,
                        employment: {
                          ...zData.employment,
                          employer: "",
                          workPosition: "",
                          worktypes: [],
                        },
                      });
                    }}
                    value={field.value}
                  >
                    {employments.isLoading
                      ? [1, 2, 3, 4, 5, 6].map((el) => {
                          return (
                            <FormItem
                              className={cn(
                                "flex items-center justify-between gap-1 border-b border-solid border-gray-200",
                                "last:border-b-0 relative"
                              )}
                              key={el}
                            >
                              <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 p-4 h-14 w-full">
                                <Skeleton className="h-5 w-[100px]" />
                              </FormLabel>
                              <FormControl className="!mt-0 right-4 absolute">
                                <Skeleton className="h-5 w-5 rounded-full" />
                              </FormControl>
                            </FormItem>
                          );
                        })
                      : employments.data?.statuses.map((el) => {
                          return (
                            <FormItem
                              className={cn(
                                "flex items-center justify-between border-b border-solid border-gray-200",
                                "last:border-b-0 relative"
                              )}
                              key={el.employmentStatusCode}
                            >
                              <FormLabel className="!mt-0 text-sm font-normal leading-6 text-gray-700 cursor-pointer p-4 h-14 w-full">
                                {el.employmentStatus}
                              </FormLabel>
                              <FormControl className="!mt-0 right-4 absolute">
                                <RadioGroupItem
                                  value={el.employmentStatusCode}
                                />
                              </FormControl>
                            </FormItem>
                          );
                        })}
                  </RadioGroup>
                </FormItem>
              );
            }}
          />
          <div className="z-10 max-md:fixed max-md:bottom-0 max-md:left-0 max-md:right-0">
            <div className="rounded-6 w-full self-end bg-white py-8 max-md:px-4 ">
              <Button
                className="w-full"
                disabled={!form.formState.isValid}
                type="submit"
              >
                {t("continue")}
              </Button>
            </div>
          </div>
        </form>
      </Form>
    </Container>
  );
}

export default Employment;
